import { trim } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';
import { formatPriceInput } from '@dashboard/services/formatting';
import FormError from '@dashboard/components/Formik/components/FormError';
import { Form } from '@dashboard/components/Formik';
import { saveCoupon } from './helpers';
import PageMultiSelect from '@/dashboard/components/Formik/components/PageMultiSelect';
import Button from '@/dashboard/components/Button';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { useToast } from '@dashboard/context/ToastContext';
import { useAuth } from '@/dashboard/context/AuthContext/AuthContext';

const DiscountsCouponsCreate = () => {
  const { seller, sellerId, token } = useAuth();
  const history = useHistory();
  const { emitToast } = useToast();

  return (
    <div className="max-w-screen-2xl mx-auto mb-36 px-4 sm:px-16 mt-8">
      <div className="absolute top-6 left-4">
        <Button appearance="light" to="/coupons">
          <ChevronLeftIcon className="w-5 h-5" />
        </Button>
      </div>

      <div className="max-w-lg mx-auto mt-20 mb-20">
        <div className="mb-6 text-center">
          <div className="leading-tight font-bold text-gray-900 text-2xl">Create a coupon</div>
          <div className="text-base font-normal">Let customers redeem a discount at checkout</div>
        </div>

        <Form
          showState={false}
          submitButton={{ label: 'Create coupon', size: 'full', arrowRight: true }}
          validationSchema={Yup.object().shape({
            label: Yup.string().required('Label is required'),
            code: Yup.string().required('Code is required'),
            discountType: Yup.string().required(),
            duration: Yup.string().required('Duration is required'),
            durationInMonths: Yup.number().when('duration', {
              is: duration => duration === 'repeating',
              then: Yup.number().required(
                'Duration in months is required when duration is repeating',
              ),
            }),
            appliesToSetupFee: Yup.boolean(),
            amountOff: Yup.number()
              .nullable()
              .when('discountType', {
                is: 'amountOff',
                then: Yup.number()
                  .moreThan(0, 'Amount off must be more than 0')
                  .required('Amount off is required')
                  .typeError('Amount off should be a number'),
              }),
            percentOff: Yup.number()
              .nullable()
              .when('discountType', {
                is: 'percentOff',
                then: Yup.number()
                  .moreThan(0, 'Percentage off must be more than 0')
                  .max(100, 'Percentage off must be less than or equal to 100')
                  .typeError('Percentage off should be a number between 1 and 100')
                  .required('Percentage off is required'),
              }),
            currency: Yup.string().when('discountType', {
              is: 'amountOff',
              then: Yup.string().required('Currency is required when using amount off'),
            }),
            maxRedemptions: Yup.number()
              .min(0, 'Max redemptions must be at least 0')
              .nullable()
              .optional(),
          })}
          initialValues={{
            label: '',
            code: '',
            pages: null,
            ticketTypes: null,
            discountType: 'amountOff',
            duration: 'forever',
            durationInMonths: 1,
            amountOff: null,
            percentOff: null,
            currency: 'USD',
            appliesToSetupFee: false,
            redeemByDate: null,
            redeemByTime: null,
            maxRedemptions: null,
          }}
          onSubmit={async (values, { setSubmitting, setStatus, resetForm }) => {
            setStatus(undefined);

            try {
              const pages = values.pages;
              const ticketTypes = values.ticketTypes;

              const redeemByDateAndTime = {
                redeemByDate: values.redeemByDate,
                redeemByTime: values.redeemByTime,
              };

              const maxRedemptions = values.maxRedemptions;

              const body = {
                ...values,
                code: trim(values.code),
                pages,
                ticketTypes,
                ...redeemByDateAndTime,
                maxRedemptions,
                amountOff:
                  values.discountType === 'amountOff'
                    ? formatPriceInput(values.amountOff, values.currency)
                    : null,
                percentOff: values.discountType === 'percentOff' ? Number(values.percentOff) : null,
              };

              if (body.redeemByDate) {
                body.redeemBy = new Date(`${body.redeemByDate} ${body.redeemByTime}`);
              }

              const response = await saveCoupon(body, sellerId, token);

              if (!response.data) {
                throw response;
              }

              history.push(`/coupons/${response.data._id}`);
              emitToast({
                title: 'Coupon created',
                status: 'success',
              });

              setStatus('success');
              resetForm({ values: response.data });
            } catch (err) {
              emitToast({
                title: 'Failed to create coupon',
                description: err?.message || 'Please try again or contact support.',
                status: 'error',
              });
              setStatus({ error: err.message || 'Something went wrong' });
            }

            setSubmitting(false);
          }}
          fields={({ values, setFieldValue, setFieldTouched, setFieldError }) => {
            return [
              {
                name: 'label',
                label: 'Label',
                required: true,
                type: 'text',
                placeholder: 'Coupon label',
                helpText: 'Internal name for this coupon.',
              },
              {
                name: 'code',
                label: 'Code',
                required: true,
                type: 'text',
                helpText: 'The code customers enter to redeem.',
                placeholder: 'Coupon code',
              },
              {
                name: 'discountType',
                required: true,
                type: 'radioCard',
                options: [
                  { label: 'Fixed amount', value: 'amountOff' },
                  { label: 'Percentage', value: 'percentOff' },
                ],
                onChange: e => {
                  if (e.target?.value === 'amountOff') {
                    setFieldValue('discountType', 'amountOff');
                    setFieldValue('percentOff', '');
                    setFieldTouched('percentOff', false, false);
                    setFieldError('percentOff', '');
                  } else if (e.target?.value === 'percentOff') {
                    setFieldValue('discountType', 'percentOff');
                    setFieldValue('amountOff', '');
                    setFieldTouched('amountOff', false, false);
                    setFieldError('amountOff', '');
                  }
                },
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'amountOff',
                    label: 'Discount amount',
                    type: 'text',
                    placeholder: 'Discount amount',
                  },
                  {
                    name: 'currency',
                    label: 'Currency',
                    type: 'currency',
                  },
                ],
                visible: values.discountType === 'amountOff',
              },
              {
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    name: 'percentOff',
                    label: 'Discount %',
                    type: 'text',
                    placeholder: 'Discount %',
                  },
                ],
                visible: values.discountType === 'percentOff',
              },
              {
                type: 'checkbox',
                name: 'appliesToSetupFee',
                label: 'Apply discount to setup fee',
              },
              {
                type: 'custom',
                renderField: () => (
                  <h3 className="text-lg font-bold text-gray-900">Limit to pages</h3>
                ),
              },
              ...(seller?.labs?.couponTicketTypes === true
                ? [
                    {
                      type: 'custom',
                      label: 'Pages',
                      renderField: () => {
                        return (
                          <>
                            <PageMultiSelect
                              placeholder="Choose pages"
                              includeTicketTypes={true}
                              initialValue={[]}
                              helpText={'Select the pages this coupon can be used on.'}
                              onChange={({ pages, tickets }) => {
                                const uniquePageIds = Array.from(pages.keys());
                                const uniqueTicketTypeIds = [
                                  ...new Set(
                                    Array.from(tickets.values()).flatMap(tickets => tickets),
                                  ),
                                ];
                                values.pages = uniquePageIds;
                                values.ticketTypes = uniqueTicketTypeIds;
                              }}
                              pageTypes={['checkout', 'event']}
                            />

                            <FormError name="pages" />
                          </>
                        );
                      },
                    },
                  ]
                : [
                    {
                      type: 'custom',
                      label: 'Pages',
                      renderField: () => {
                        return (
                          <>
                            <PageMultiSelect
                              placeholder="Choose pages"
                              includeTicketTypes={false}
                              initialValue={[]}
                              helpText={'Select the pages this coupon can be used on.'}
                              onChange={({ pages }) => {
                                const uniquePageIds = Array.from(pages.keys());
                                values.pages = uniquePageIds;
                              }}
                              pageTypes={['checkout', 'event']}
                            />

                            <FormError name="pages" />
                          </>
                        );
                      },
                    },
                  ]),
              {
                type: 'custom',
                renderField: () => (
                  <h3 className="text-lg font-bold text-gray-900">Redemption limits</h3>
                ),
              },
              {
                type: 'number',
                placeholder: 'Max times used',
                label: 'Max uses',
                helpText:
                  "The total amount of times this coupon can be used. Doesn't limit a single customer from redeeming multiple times.",
                name: 'maxRedemptions',
              },
              {
                label: 'Expires on',
                type: 'columns',
                columns: 2,
                gap: 'sm',
                fields: [
                  {
                    type: 'date',
                    name: 'redeemByDate',
                    onChange: e => {
                      setFieldValue('redeemByDate', e.target?.value);

                      if (e.target.value && !values.redeemByTime) {
                        setFieldValue('redeemByTime', '00:00');
                      }
                      if (e.target.value === '') {
                        setFieldValue('redeemByTime', '');
                      }
                    },
                  },
                  {
                    type: 'time',
                    name: 'redeemByTime',
                  },
                ],
                helpText: `Date and time the coupon must be used before. Leave blank for coupons that don't expire.`,
              },
              {
                type: 'select',
                name: 'duration',
                label: 'Subscription duration',
                helpText: 'How long the discount lasts for subscriptions.',
                options: [
                  {
                    value: 'forever',
                    label: 'Every payment',
                  },
                  {
                    value: 'once',
                    label: 'First payment only',
                  },
                  {
                    value: 'repeating',
                    label: 'Set number of months',
                  },
                ],
              },
              {
                type: 'columns',
                columns: 2,
                fields: [
                  {
                    type: 'number',
                    label: 'Number of months',
                    name: 'durationInMonths',
                    helpText: 'How many months the discount will apply.',
                  },
                ],
                visible: values.duration === 'repeating',
              },
            ];
          }}
        ></Form>
      </div>
    </div>
  );
};

DiscountsCouponsCreate.propTypes = {
  history: PropTypes.object.isRequired,
};

export default DiscountsCouponsCreate;
